import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';

const POLL_MS = 60_000;

type RemoteVersionPayload = {
  version?: string;
  buildId?: string;
  builtAt?: string;
};

function versionCheckUrl(): string {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return '/app-version.json';
  const base = window.location.origin || '';
  return `${base}/app-version.json`;
}

/** Webpack sustituye __APP_BUILD_ID__ en export web; en dev con Metro puede no existir el identificador. */
function getEmbeddedBuildId(): string {
  if (typeof __APP_BUILD_ID__ !== 'undefined') {
    return __APP_BUILD_ID__;
  }
  return 'development';
}

export function WebAppUpdatePrompt() {
  const [open, setOpen] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState<string | null>(null);
  const shownRef = useRef(false);

  const check = useCallback(async () => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const embeddedId = getEmbeddedBuildId();
    if (embeddedId === 'development') return;

    try {
      const res = await fetch(`${versionCheckUrl()}?t=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const data = (await res.json()) as RemoteVersionPayload;
      if (!data?.buildId || data.buildId === embeddedId) return;
      if (shownRef.current) return;
      shownRef.current = true;
      setRemoteVersion(data.version ?? data.buildId);
      setOpen(true);
    } catch {
      // ignorar red / JSON inválido
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (getEmbeddedBuildId() === 'development') return;

    void check();
    const id = window.setInterval(() => void check(), POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [check]);

  const reload = () => {
    if (typeof window === 'undefined') return;
    const go = () => window.location.reload();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.update())))
        .finally(go);
      return;
    }
    go();
  };

  if (Platform.OS !== 'web') return null;

  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={() => {}}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.title}>Nueva versión</Text>
          <Text style={styles.body}>
            La app ha sido actualizada a la versión {remoteVersion ?? '…'}. Por favor, recarga la página.
          </Text>
          <Pressable
            style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
            onPress={reload}
          >
            <Text style={styles.buttonLabel}>Recargar</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    maxWidth: 400,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 12,
  },
  body: {
    fontSize: 15,
    lineHeight: 22,
    color: '#333',
    marginBottom: 20,
  },
  button: {
    backgroundColor: '#A20000',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  buttonPressed: {
    opacity: 0.88,
  },
  buttonLabel: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
